import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { PageHeader, SectionCard, EmptyState } from '../ui';
import { paymentService, type BonusTier } from '../../services/paymentService';

// Cấu hình cho bot Telegram (job automation): giá mỗi dòng + trần số dòng, và
// bậc bonus nạp RIÊNG của bot (scope 'bot') — tách khỏi bonus topup license của
// AgentX. Tất cả lưu trên D1, bot đọc qua /points.

const num = (v: string): number => Number(String(v).replace(',', '.'));

// ms epoch <-> giá trị <input type="datetime-local"> (giờ địa phương, không có timezone).
const toLocalInput = (ms: number): string => {
  const d = new Date(ms - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
};
const fromLocalInput = (v: string): number | null => {
  if (!v) return null;
  const ms = new Date(v).getTime();
  return Number.isFinite(ms) ? ms : null;
};

export const BotProcessSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  const [price, setPrice] = useState('');
  const [maxLines, setMaxLines] = useState('');
  // Nhập bằng PHÚT cho dễ; chuyển sang ms khi lưu. queue = 0 nghĩa là tắt.
  const [jobTimeoutMin, setJobTimeoutMin] = useState('');
  const [queueTimeoutMin, setQueueTimeoutMin] = useState('');
  // Số lần claim/chạy tối đa trước khi job lỗi-do-máy chuyển sang "chờ xử lý tay".
  const [maxAttempts, setMaxAttempts] = useState('');
  // Chế độ xử lý tay: BẬT → cạn maxAttempts đưa job về "chờ xử lý tay"; TẮT → fail luôn.
  const [manualMode, setManualMode] = useState(true);
  // Point thưởng chào mừng: BẬT → duyệt user bot lần đầu tặng point (một lần/đời).
  const [welcomeBonus, setWelcomeBonus] = useState(true);

  // Chế độ bảo trì: chặn tạo job mới + đẩy thông báo cho user đã cấp phép.
  const [maintEnabled, setMaintEnabled] = useState(false);
  const [maintMessage, setMaintMessage] = useState('');
  const [maintUntil, setMaintUntil] = useState(''); // datetime-local; rỗng = không rõ
  const [maintBroadcast, setMaintBroadcast] = useState(true);

  // Gửi thông báo chung (độc lập với bảo trì) cho mọi user đã cấp phép.
  const [announce, setAnnounce] = useState('');
  const [confirmAnnounce, setConfirmAnnounce] = useState(false);

  const [tiers, setTiers] = useState<BonusTier[]>([]);
  const [draft, setDraft] = useState<Record<number, { bonusPercent: string }>>({});
  const [newTier, setNewTier] = useState({ minAmountUsdt: '', bonusPercent: '' });

  const notify = (msg: string, sev: 'success' | 'error') => setToast({ msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, pricing] = await Promise.all([
        paymentService.getProcessConfig(),
        paymentService.getPricing('bot'),
      ]);
      setPrice(String(cfg.pricePerLine));
      setMaxLines(String(cfg.maxLines));
      setJobTimeoutMin(String(cfg.jobTimeoutMs / 60000));
      setQueueTimeoutMin(String(cfg.queueTimeoutMs / 60000));
      setMaxAttempts(String(cfg.maxAttempts));
      setManualMode(cfg.manualModeEnabled);
      setWelcomeBonus(cfg.welcomeBonusEnabled);
      setMaintEnabled(cfg.maintenanceEnabled);
      setMaintMessage(cfg.maintenanceMessage ?? '');
      setMaintUntil(cfg.maintenanceUntil ? toLocalInput(cfg.maintenanceUntil) : '');
      setTiers(pricing.tiers);
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveConfig = async () => {
    const pricePerLine = num(price);
    const ml = parseInt(maxLines, 10);
    if (!Number.isFinite(pricePerLine) || pricePerLine <= 0) return notify('Giá mỗi dòng phải > 0', 'error');
    if (!Number.isInteger(ml) || ml <= 0) return notify('Trần số dòng phải là số nguyên > 0', 'error');
    setBusy(true);
    try {
      await paymentService.setProcessConfig({ pricePerLine, maxLines: ml });
      notify('Đã lưu cấu hình process.', 'success');
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveTimeouts = async () => {
    const jobMin = num(jobTimeoutMin);
    const qMin = num(queueTimeoutMin);
    const attempts = parseInt(maxAttempts, 10);
    if (!Number.isFinite(jobMin) || jobMin < 1) return notify('Timeout job phải ≥ 1 phút', 'error');
    if (!Number.isFinite(qMin) || qMin < 0) return notify('Timeout hàng đợi không hợp lệ (0 = tắt)', 'error');
    if (qMin > 0 && qMin < 1) return notify('Timeout hàng đợi phải là 0 hoặc ≥ 1 phút', 'error');
    if (!Number.isInteger(attempts) || attempts < 1 || attempts > 10) return notify('Số lần thử phải từ 1 đến 10', 'error');
    setBusy(true);
    try {
      await paymentService.setProcessConfig({
        jobTimeoutMs: Math.round(jobMin * 60000),
        queueTimeoutMs: Math.round(qMin * 60000),
        maxAttempts: attempts,
      });
      notify('Đã lưu timeout.', 'success');
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Bật/tắt chế độ xử lý tay. BẬT: job cạn maxAttempts -> "chờ xử lý tay" (chờ admin).
  // TẮT: cạn maxAttempts -> failed luôn (+ hoàn point dòng đó như mọi job failed).
  const saveManualMode = async (enable: boolean) => {
    setManualMode(enable); // optimistic
    setBusy(true);
    try {
      await paymentService.setProcessConfig({ manualModeEnabled: enable });
      notify(enable ? 'Đã bật chế độ xử lý tay.' : 'Đã tắt — job cạn số lần thử sẽ fail luôn.', 'success');
    } catch (e) {
      setManualMode(!enable); // revert khi lỗi
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Bật/tắt point thưởng chào mừng. BẬT: user bot được duyệt lần đầu nhận point
  // chào mừng (một lần/đời). TẮT: duyệt user không cộng point.
  const saveWelcomeBonus = async (enable: boolean) => {
    setWelcomeBonus(enable); // optimistic
    setBusy(true);
    try {
      await paymentService.setProcessConfig({ welcomeBonusEnabled: enable });
      notify(enable ? 'Đã bật thưởng point chào mừng.' : 'Đã tắt — duyệt user sẽ không cộng point.', 'success');
    } catch (e) {
      setWelcomeBonus(!enable); // revert khi lỗi
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Bật/tắt bảo trì. Khi BẬT: bắt buộc có nội dung; broadcast tuỳ ô tick.
  // Khi TẮT: không broadcast (tránh spam); chỉ gỡ cờ chặn job.
  const saveMaintenance = async (enable: boolean) => {
    const msg = maintMessage.trim();
    if (enable && !msg) return notify('Nhập nội dung thông báo bảo trì trước khi bật.', 'error');
    const until = fromLocalInput(maintUntil);
    if (enable && until !== null && until <= Date.now()) return notify('Thời điểm dự kiến xong phải ở tương lai.', 'error');
    setBusy(true);
    try {
      const { broadcast } = await paymentService.setProcessConfig(
        { maintenanceEnabled: enable, maintenanceMessage: msg || null, maintenanceUntil: until },
        enable ? maintBroadcast : false,
      );
      notify(
        enable
          ? `Đã bật bảo trì${maintBroadcast ? ` · đã gửi thông báo cho ${broadcast} user` : ''}.`
          : 'Đã tắt bảo trì.',
        'success',
      );
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  // Gửi thông báo chung cho mọi user đã cấp phép (qua dialog xác nhận).
  const sendAnnounce = async () => {
    const msg = announce.trim();
    if (!msg) return notify('Nhập nội dung thông báo.', 'error');
    setBusy(true);
    try {
      const { broadcast } = await paymentService.broadcastBotMessage(msg);
      notify(`Đã gửi thông báo cho ${broadcast} user.`, 'success');
      setAnnounce('');
      setConfirmAnnounce(false);
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveTier = async (minAmountUsdt: number, bonusPercent: number, isActive: boolean) => {
    setBusy(true);
    try {
      await paymentService.setTier({ minAmountUsdt, bonusPercent, isActive, scope: 'bot' });
      notify('Đã lưu bậc bonus (bot).', 'success');
      setDraft((d) => {
        const next = { ...d };
        delete next[minAmountUsdt];
        return next;
      });
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const addTier = async () => {
    const minAmountUsdt = num(newTier.minAmountUsdt);
    const bonusPercent = num(newTier.bonusPercent);
    if (!Number.isFinite(minAmountUsdt) || minAmountUsdt < 0) return notify('Mức nạp tối thiểu không hợp lệ', 'error');
    if (!Number.isFinite(bonusPercent) || bonusPercent < 0) return notify('% bonus không hợp lệ', 'error');
    await saveTier(minAmountUsdt, bonusPercent, true);
    setNewTier({ minAmountUsdt: '', bonusPercent: '' });
  };

  const removeTier = async (minAmountUsdt: number) => {
    if (minAmountUsdt === 0) return notify('Không xoá được bậc đáy 0.', 'error');
    setBusy(true);
    try {
      await paymentService.removeTier(minAmountUsdt, 'bot');
      notify('Đã xoá bậc bonus.', 'success');
      await load();
    } catch (e) {
      notify((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader title="Bot Process" description="Giá xử lý & bonus nạp cho bot Telegram (lưu trên D1)" />

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack spacing={3} sx={{ mt: 2 }}>
          <SectionCard
            title="Bảo trì / nâng cấp hệ thống"
            description="Khi bật: bot & API không nhận job mới (job đang chạy vẫn tiếp tục) và đẩy thông báo cho user đã cấp phép."
          >
            <Stack spacing={2}>
              <Alert severity={maintEnabled ? 'warning' : 'success'}>
                {maintEnabled ? 'ĐANG BẢO TRÌ — job mới bị từ chối (503).' : 'Hệ thống đang hoạt động bình thường.'}
              </Alert>
              <TextField
                label="Nội dung thông báo (gửi cho user)"
                placeholder="Ví dụ: Hệ thống đang nâng cấp, dự kiến hoạt động lại sau 30 phút. Mong bạn thông cảm."
                value={maintMessage}
                onChange={(e) => setMaintMessage(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                size="small"
                inputProps={{ maxLength: 2000 }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <TextField
                  label="Dự kiến xong (tuỳ chọn)"
                  type="datetime-local"
                  value={maintUntil}
                  onChange={(e) => setMaintUntil(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText="Dùng để báo Retry-After cho API"
                  sx={{ maxWidth: 260 }}
                />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ pt: 0.5 }}>
                  <Switch checked={maintBroadcast} onChange={(e) => setMaintBroadcast(e.target.checked)} />
                  <Typography variant="body2">Gửi thông báo cho user đã cấp phép khi bật</Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2}>
                {maintEnabled ? (
                  <>
                    <Button variant="outlined" onClick={() => saveMaintenance(true)} disabled={busy}>
                      Cập nhật nội dung / gửi lại
                    </Button>
                    <Button variant="contained" color="success" onClick={() => saveMaintenance(false)} disabled={busy}>
                      Tắt bảo trì
                    </Button>
                  </>
                ) : (
                  <Button variant="contained" color="warning" onClick={() => saveMaintenance(true)} disabled={busy}>
                    Bật bảo trì
                  </Button>
                )}
              </Stack>

              <Divider />

              <Typography variant="subtitle2">Gửi thông báo cho user</Typography>
              <Typography variant="caption" color="text.secondary">
                Gửi một thông báo bất kỳ tới TẤT CẢ user bot đã cấp phép (độc lập với bảo trì).
              </Typography>
              <TextField
                label="Nội dung thông báo"
                placeholder="Ví dụ: Khuyến mãi nạp point +20% đến hết Chủ Nhật!"
                value={announce}
                onChange={(e) => setAnnounce(e.target.value)}
                multiline
                minRows={2}
                fullWidth
                size="small"
                inputProps={{ maxLength: 2000 }}
              />
              <Stack direction="row">
                <Button
                  variant="contained"
                  onClick={() => setConfirmAnnounce(true)}
                  disabled={busy || !announce.trim()}
                >
                  Gửi thông báo
                </Button>
              </Stack>
            </Stack>
          </SectionCard>

          <SectionCard title="Giá xử lý (process)">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                label="Giá mỗi dòng (point)"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                size="small"
                sx={{ maxWidth: 200 }}
              />
              <TextField
                label="Trần số dòng / lần"
                value={maxLines}
                onChange={(e) => setMaxLines(e.target.value)}
                size="small"
                sx={{ maxWidth: 200 }}
              />
              <Button
                variant="contained"
                startIcon={<SaveOutlinedIcon />}
                onClick={saveConfig}
                disabled={busy}
              >
                Lưu
              </Button>
            </Stack>
          </SectionCard>

          <SectionCard
            title="Thời gian chờ (timeout)"
            description="Quá hạn thì job tự chuyển 'failed' và hoàn point. Nhập theo phút."
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
              <TextField
                label="Job timeout (phút)"
                helperText="Từ lúc máy nhận tới khi phải trả kết quả"
                value={jobTimeoutMin}
                onChange={(e) => setJobTimeoutMin(e.target.value)}
                size="small"
                sx={{ maxWidth: 240 }}
              />
              <TextField
                label="Queue timeout (phút, 0 = tắt)"
                helperText="Job chờ máy nhận quá lâu thì bỏ"
                value={queueTimeoutMin}
                onChange={(e) => setQueueTimeoutMin(e.target.value)}
                size="small"
                sx={{ maxWidth: 260 }}
              />
              <TextField
                label="Số lần thử (maxAttempts)"
                helperText="Lỗi-do-máy nhả cho máy khác; cạn số lần → chờ xử lý tay (hoặc fail nếu tắt Manual)"
                value={maxAttempts}
                onChange={(e) => setMaxAttempts(e.target.value)}
                size="small"
                sx={{ maxWidth: 240 }}
              />
              <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={saveTimeouts} disabled={busy}>
                Lưu
              </Button>
            </Stack>
          </SectionCard>

          <SectionCard
            title="Chế độ xử lý tay (Manual)"
            description="Quyết định số phận job khi đã cạn số lần thử (maxAttempts) vì lỗi-do-máy / timeout."
          >
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Switch checked={manualMode} onChange={(e) => saveManualMode(e.target.checked)} disabled={busy} />
                <Typography variant="body1">{manualMode ? 'Đang BẬT' : 'Đang TẮT'}</Typography>
              </Stack>
              <Alert severity={manualMode ? 'info' : 'warning'}>
                {manualMode
                  ? 'BẬT: job cạn số lần thử sẽ chuyển sang "Cần xử lý tay" để admin xử lý (lấy link / đánh dấu fail).'
                  : 'TẮT: job cạn số lần thử sẽ trả "failed" luôn (tự hoàn point dòng đó), không đưa vào hàng chờ xử lý tay.'}
              </Alert>
            </Stack>
          </SectionCard>

          <SectionCard
            title="Point thưởng chào mừng"
            description="Khi admin cấp phép một user bot lần đầu ở trang Chờ duyệt, tự động tặng point chào mừng (một lần/đời user)."
          >
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Switch checked={welcomeBonus} onChange={(e) => saveWelcomeBonus(e.target.checked)} disabled={busy} />
                <Typography variant="body1">{welcomeBonus ? 'Đang BẬT' : 'Đang TẮT'}</Typography>
              </Stack>
              <Alert severity={welcomeBonus ? 'info' : 'warning'}>
                {welcomeBonus
                  ? 'BẬT: user được cấp phép lần đầu nhận point thưởng và được thông báo. Duyệt lại user cũ không cộng thêm.'
                  : 'TẮT: cấp phép user không cộng point nào.'}
              </Alert>
            </Stack>
          </SectionCard>

          <SectionCard
            title="Bậc bonus nạp (bot)"
            description="Riêng cho bot, không ảnh hưởng bonus topup license của AgentX."
          >
            {tiers.length === 0 ? (
              <EmptyState title="Chưa có bậc bonus nào" />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nạp tối thiểu (USDT)</TableCell>
                      <TableCell>Bonus (%)</TableCell>
                      <TableCell align="center">Bật</TableCell>
                      <TableCell align="right">Thao tác</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tiers.map((t) => {
                      const d = draft[t.minAmountUsdt];
                      const pct = d ? d.bonusPercent : String(t.bonusPercent);
                      return (
                        <TableRow key={t.minAmountUsdt}>
                          <TableCell>{t.minAmountUsdt}</TableCell>
                          <TableCell>
                            <TextField
                              value={pct}
                              onChange={(e) =>
                                setDraft((prev) => ({ ...prev, [t.minAmountUsdt]: { bonusPercent: e.target.value } }))
                              }
                              size="small"
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={t.isActive}
                              onChange={(e) => saveTier(t.minAmountUsdt, num(pct), e.target.checked)}
                              disabled={busy}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Lưu">
                              <span>
                                <IconButton
                                  onClick={() => saveTier(t.minAmountUsdt, num(pct), t.isActive)}
                                  disabled={busy || !d}
                                  size="small"
                                >
                                  <SaveOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Xoá">
                              <span>
                                <IconButton
                                  onClick={() => removeTier(t.minAmountUsdt)}
                                  disabled={busy || t.minAmountUsdt === 0}
                                  size="small"
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems="center">
              <TextField
                label="Nạp tối thiểu (USDT)"
                value={newTier.minAmountUsdt}
                onChange={(e) => setNewTier((p) => ({ ...p, minAmountUsdt: e.target.value }))}
                size="small"
                sx={{ width: 180 }}
              />
              <TextField
                label="Bonus (%)"
                value={newTier.bonusPercent}
                onChange={(e) => setNewTier((p) => ({ ...p, bonusPercent: e.target.value }))}
                size="small"
                sx={{ width: 140 }}
              />
              <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={addTier} disabled={busy}>
                Thêm bậc
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Bonus = số USDT nạp × (1 + %/100). Bậc khớp là mức nạp tối thiểu lớn nhất ≤ số tiền nạp.
            </Typography>
          </SectionCard>
        </Stack>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>

      <Dialog open={confirmAnnounce} onClose={() => setConfirmAnnounce(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Gửi thông báo cho tất cả user?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Thông báo sau sẽ được gửi tới <b>mọi user bot đã cấp phép</b>. Không thể thu hồi.
          </DialogContentText>
          <Alert severity="info" icon={false} sx={{ whiteSpace: 'pre-wrap' }}>
            📢 {announce.trim()}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAnnounce(false)}>Huỷ</Button>
          <Button variant="contained" onClick={() => void sendAnnounce()} disabled={busy}>
            Gửi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
